import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useOutletContext } from 'react-router-dom';
import { ArrowLeft, Save, Eye, Loader, CheckCircle, AlertTriangle } from 'lucide-react';
import { supabase } from '../../utils/supabaseClient';
import RichTextEditor, { normalizeSpaces } from '../ui/RichTextEditor';

/**
 * EmailTemplateEditor
 * 
 * A dedicated page for creating and editing email templates.
 * Uses the Quill rich text editor for the email body.
 */

const MERGE_VARIABLES = [
  { key: '{artist_name}', description: 'Artist name', example: 'Jane Smith' },
  { key: '{first_name}', description: 'First name', example: 'Jane' },
  { key: '{collection_name}', description: 'Collection name', exampleKey: 'collection' },
  { key: '{organization_name}', description: 'Organization', exampleKey: 'organization' },
  { key: '{accepted_artworks}', description: 'Accepted artworks', example: '• "Sunset Dreams" - $500 (24x36)' },
  { key: '{important_dates}', description: 'Important dates', example: 'Drop-off: Jan 15...' },
  { key: '{acceptance_url}', description: 'Acceptance link', example: 'https://...' },
  { key: '{event_readiness_url}', description: 'Event readiness link', example: 'https://vibrantcast.com/event-readiness?...' },
  { key: '{submission_apply_url}', description: 'Apply to show link', example: 'https://vibrantcast.com/artist/submissions/.../apply' },
  { key: '{drop_off_date}', description: 'Drop-off date', example: 'January 15, 2025' },
  { key: '{drop_off_location}', description: 'Drop-off location', example: '123 Gallery St' },
  { key: '{pickup_date}', description: 'Pickup date', example: 'February 28, 2025' },
  { key: '{pickup_location}', description: 'Pickup location', example: '123 Gallery St' },
  { key: '{contact_email}', description: 'Contact email', exampleKey: 'contact_email' },
  { key: '{organization_logo}', description: 'Logo URL', example: 'https://...' },
  { key: '{submission_deadline}', description: 'Deadline', example: 'December 31, 2024' },
  { key: '{submission_id}', description: 'Submission ID', example: '123' },
];

const AUDIENCE_TYPES = [
  { value: 'accepted_artists', label: 'Accepted Artists', description: 'Artists with accepted artwork' },
  { value: 'not_accepted', label: 'Not Accepted', description: 'Artists whose work was not selected' },
  { value: 'no_artwork_submitted', label: 'No Artwork Submitted', description: 'Artists who registered but haven\'t submitted' },
  { value: 'all_artists', label: 'All Artists', description: 'Everyone in the submission' },
  { value: 'unpaid_artists', label: 'Unpaid Artists', description: 'Artists who haven\'t paid yet' },
  { value: 'needs_terms', label: 'Needs Terms', description: 'Accepted artists who haven\'t agreed to terms yet' },
  { value: 'checked_in_artists', label: '✅ Checked-In Artists', description: 'Accepted artists with artwork checked in' },
  { value: 'not_checked_in_artists', label: '📦 Not Checked-In Yet', description: 'Accepted artists whose artwork hasn\'t been checked in' },
  { value: 'organization_contacts', label: 'Organization Contacts', description: 'Invite contacts to apply' },
];

const EmailTemplateEditor = () => {
  const navigate = useNavigate();
  const { templateId } = useParams();
  const outletContext = useOutletContext() || {};
  const { selectedOrganization, organizationMemberships } = outletContext;
  
  // Debug logging
  useEffect(() => {
    console.log('EmailTemplateEditor - Outlet Context:', outletContext);
    console.log('EmailTemplateEditor - Selected Organization:', selectedOrganization);
    console.log('EmailTemplateEditor - Organization Memberships:', organizationMemberships);
  }, [outletContext, selectedOrganization, organizationMemberships]);
  
  // If no selectedOrganization but we have memberships, use the first one
  const effectiveOrganization = selectedOrganization || 
    (organizationMemberships && organizationMemberships.length > 0 ? organizationMemberships[0].organizations : null);
  
  const isEditMode = !!templateId;
  
  const [loading, setLoading] = useState(isEditMode);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  
  // Form state
  const [templateKey, setTemplateKey] = useState('');
  const [subject, setSubject] = useState('');
  const [htmlBody, setHtmlBody] = useState('');
  const [audienceType, setAudienceType] = useState('all_artists');
  const [description, setDescription] = useState('');
  
  // Preview state
  const [showPreview, setShowPreview] = useState(false);

  // Helper to get example value for a merge variable
  const getExampleValue = (mv) => {
    if (mv.exampleKey === 'organization') {
      return effectiveOrganization?.name || 'Your Organization';
    }
    if (mv.exampleKey === 'contact_email') {
      return effectiveOrganization?.contact_email || 'contact@example.com';
    }
    if (mv.exampleKey === 'collection') {
      return 'Art Speaks 2025'; // Could be enhanced to show actual collection if available
    }
    return mv.example || '';
  };

  useEffect(() => {
    if (isEditMode && templateId) {
      loadTemplate();
    }
  }, [templateId]);

  const loadTemplate = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('organization_email_templates')
        .select('*')
        .eq('id', templateId)
        .single();
      
      if (error) throw error;
      
      if (data) {
        setTemplateKey(data.template_key || '');
        setSubject(data.subject || '');
        setHtmlBody(data.html_body || '');
        setAudienceType(data.audience_type || 'all_artists');
        setDescription(data.description || '');
      }
    } catch (err) {
      console.error('Error loading template:', err);
      setError('Failed to load template');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!templateKey || !subject || !htmlBody) {
      setError('Please fill in all required fields');
      return;
    }
    
    const organizationId = effectiveOrganization?.organization_id || effectiveOrganization?.id;
    if (!organizationId) {
      setError('No organization selected');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const templateData = {
        organization_id: organizationId,
        template_key: templateKey.toLowerCase().replace(/\s+/g, '_'),
        subject,
        html_body: normalizeSpaces(htmlBody),
        audience_type: audienceType,
        updated_at: new Date().toISOString()
      };

      if (isEditMode) {
        const { error } = await supabase
          .from('organization_email_templates')
          .update(templateData)
          .eq('id', templateId);
        
        if (error) throw error;
      } else {
        templateData.created_at = new Date().toISOString();
        const { error } = await supabase
          .from('organization_email_templates')
          .insert(templateData);
        
        if (error) throw error;
      }

      setSuccess(true);
      setTimeout(() => {
        navigate('/curator/communications');
      }, 1500);
    } catch (err) {
      console.error('Error saving template:', err);
      setError(err.message || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const insertMergeVar = (key) => {
    // For subject field, we append to the current value
    // For body, the user can click to copy and paste into the editor
    navigator.clipboard.writeText(key);
  };

  const getPreviewHtml = () => {
    let preview = htmlBody
      .replace(/{artist_name}/g, 'Jane Artist')
      .replace(/{first_name}/g, 'Jane')
      .replace(/{collection_name}/g, 'Art Speaks: Abstract Expressions')
      .replace(/{organization_name}/g, effectiveOrganization?.name || 'VibrantCast')
      .replace(/{accepted_artworks}/g, '<ul><li>"Sunset Dreams" - $500 (24x36)</li><li>"Urban Flow" - $750 (48x48)</li></ul>')
      .replace(/{important_dates}/g, '<ul><li>Drop-off: Jan 15, 2025</li><li>Opening: Jan 20, 2025</li><li>Pickup: Feb 15, 2025</li></ul>')
      .replace(/{acceptance_url}/g, 'https://vibrantcast.com/acceptance/demo')
      .replace(/{event_readiness_url}/g, 'https://vibrantcast.com/event-readiness?collectionId=349&eventId=186&artistUuid=demo-artist')
      .replace(/{drop_off_date}/g, 'January 15, 2025')
      .replace(/{drop_off_location}/g, '123 Gallery Street, Art District')
      .replace(/{pickup_date}/g, 'February 15, 2025')
      .replace(/{pickup_location}/g, '123 Gallery Street, Art District')
      .replace(/{contact_email}/g, 'info@example.com')
      .replace(/{organization_logo}/g, '')
      .replace(/{submission_deadline}/g, 'December 31, 2024')
      .replace(/{submission_id}/g, '13');
    
    return preview;
  };

  if (loading) {
    return (
      <div style={{ padding: '2rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Loader className="animate-spin" size={24} />
        <span style={{ marginLeft: '0.5rem' }}>Loading template...</span>
      </div>
    );
  }

  return (
    <div style={{ padding: '1.5rem', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Organization Warning */}
      {!effectiveOrganization && (
        <div style={{ 
          padding: '1rem', 
          marginBottom: '1rem', 
          backgroundColor: '#fef2f2', 
          border: '1px solid #fecaca', 
          borderRadius: '0.5rem', 
          display: 'flex', 
          alignItems: 'center', 
          gap: '0.5rem' 
        }}>
          <AlertTriangle size={20} style={{ color: '#dc2626' }} />
          <span style={{ color: '#dc2626' }}>No organization selected</span>
        </div>
      )}

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <button
            onClick={() => navigate('/curator/communications')}
            style={{ 
              padding: '0.5rem', 
              background: 'none', 
              border: '1px solid #e5e7eb', 
              borderRadius: '0.375rem', 
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <ArrowLeft size={20} />
          </button>
          <h1 style={{ fontSize: '1.5rem', fontWeight: '600', color: '#111827', margin: 0 }}>
            {isEditMode ? 'Edit Email Template' : 'Create Email Template'}
          </h1>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <button
            onClick={() => setShowPreview(!showPreview)}
            style={{
              padding: '0.5rem 1rem',
              border: '1px solid #e5e7eb',
              borderRadius: '0.375rem',
              background: 'white',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            <Eye size={16} />
            {showPreview ? 'Hide Preview' : 'Preview'}
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              padding: '0.5rem 1rem',
              borderRadius: '0.375rem',
              border: 'none',
              background: saving ? '#d1d5db' : '#7c3aed',
              color: 'white',
              cursor: saving ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            {saving ? <Loader size={16} className="animate-spin" /> : <Save size={16} />}
            {saving ? 'Saving...' : 'Save Template'}
          </button>
        </div>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div style={{ padding: '1rem', marginBottom: '1rem', backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <CheckCircle size={20} style={{ color: '#16a34a' }} />
          <span style={{ color: '#166534' }}>Template saved successfully! Redirecting...</span>
        </div>
      )}
      {error && (
        <div style={{ padding: '1rem', marginBottom: '1rem', backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <AlertTriangle size={20} style={{ color: '#dc2626' }} />
          <span style={{ color: '#dc2626' }}>{error}</span>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: showPreview ? '1fr 1fr' : '1fr', gap: '1.5rem' }}>
        {/* Form */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          {/* Template Key */}
          <div>
            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>
              Template Name *
            </label>
            <input
              type="text"
              value={templateKey}
              onChange={(e) => setTemplateKey(e.target.value)}
              placeholder="e.g., artwork_accepted, deadline_warning"
              disabled={isEditMode}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                backgroundColor: isEditMode ? '#f9fafb' : 'white'
              }}
            />
            {isEditMode && (
              <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>Template name cannot be changed after creation</p>
            )}
          </div>

          {/* Recipients */}
          <div>
            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>
              Recipients *
            </label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
              {AUDIENCE_TYPES.map((at) => (
                <button
                  key={at.value}
                  type="button"
                  onClick={() => setAudienceType(at.value)}
                  title={at.description}
                  style={{
                    padding: '0.375rem 0.75rem',
                    fontSize: '0.8125rem',
                    border: audienceType === at.value ? '2px solid #7c3aed' : '1px solid #d1d5db',
                    borderRadius: '0.375rem',
                    backgroundColor: audienceType === at.value ? '#faf5ff' : 'white',
                    color: audienceType === at.value ? '#7c3aed' : '#374151',
                    fontWeight: audienceType === at.value ? '600' : '400',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease'
                  }}
                >
                  {at.label}
                </button>
              ))}
            </div>
          </div>

          {/* Description */}
          <div>
            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>
              Description (optional)
            </label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description of when to use this template"
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '0.375rem',
                fontSize: '0.875rem'
              }}
            />
          </div>

          {/* Subject */}
          <div>
            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>
              Email Subject *
            </label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="e.g., Congratulations! Your artwork has been accepted for {collection_name}"
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '0.375rem',
                fontSize: '0.875rem'
              }}
            />
          </div>

          {/* Email Body */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
              <label style={{ fontSize: '0.875rem', fontWeight: '500', color: '#374151' }}>
                Email Body *
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <select
                  onChange={(e) => {
                    if (e.target.value) {
                      navigator.clipboard.writeText(e.target.value);
                      // Show feedback
                      const select = e.target;
                      const originalColor = select.style.borderColor;
                      select.style.borderColor = '#16a34a';
                      setTimeout(() => { select.style.borderColor = originalColor; }, 500);
                      e.target.value = '';
                    }
                  }}
                  style={{
                    padding: '0.375rem 0.5rem',
                    fontSize: '0.8125rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '0.25rem',
                    backgroundColor: 'white',
                    cursor: 'pointer',
                    minWidth: '200px'
                  }}
                >
                  <option value="">+ Insert Field</option>
                  {MERGE_VARIABLES.map((mv) => {
                    const exampleVal = getExampleValue(mv);
                    return (
                      <option key={mv.key} value={mv.key}>
                        {mv.description}{exampleVal ? ` (${exampleVal})` : ''}
                      </option>
                    );
                  })}
                </select>
                <span style={{ fontSize: '0.625rem', color: '#9ca3af' }}>paste where needed</span>
              </div>
            </div>
            <RichTextEditor
              value={htmlBody}
              onChange={setHtmlBody}
              placeholder="Compose your email here. Use merge variables for personalization..."
              minHeight="300px"
            />
          </div>
        </div>

        {/* Preview Panel */}
        {showPreview && (
          <div style={{ position: 'sticky', top: '1rem', height: 'fit-content' }}>
            <div style={{ backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '0.5rem', overflow: 'hidden' }}>
              <div style={{ padding: '0.75rem 1rem', backgroundColor: '#e5e7eb', borderBottom: '1px solid #d1d5db' }}>
                <span style={{ fontSize: '0.875rem', fontWeight: '500', color: '#374151' }}>Preview</span>
              </div>
              <div style={{ padding: '1rem' }}>
                <div style={{ marginBottom: '1rem', padding: '0.5rem', backgroundColor: 'white', borderRadius: '0.25rem', border: '1px solid #e5e7eb' }}>
                  <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>Subject: </span>
                  <span style={{ fontSize: '0.875rem', color: '#111827' }}>
                    {subject
                      .replace(/{collection_name}/g, 'Art Speaks: Abstract Expressions')
                      .replace(/{organization_name}/g, selectedOrganization?.name || 'VibrantCast')
                      .replace(/{event_name}/g, 'Opening Reception')
                    }
                  </span>
                </div>
                <div 
                  style={{ 
                    backgroundColor: 'white', 
                    padding: '1rem', 
                    borderRadius: '0.25rem', 
                    border: '1px solid #e5e7eb',
                    maxHeight: '600px',
                    overflowY: 'auto'
                  }}
                  dangerouslySetInnerHTML={{ __html: getPreviewHtml() }}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailTemplateEditor;
